import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { PageHeader, fmtINR, ToolbarSelect } from "./_shared";

export default function Matching() {
  const [mode, setMode] = useState("vehicle"); // vehicle | requirement
  const [list, setList] = useState([]);
  const [selectedId, setSelectedId] = useState(null);
  const [result, setResult] = useState(null);

  useEffect(() => {
    (async () => {
      if (mode === "vehicle") {
        const { data } = await api.get("/admin/seller-leads");
        setList(data); setSelectedId(data[0]?.id || null);
      } else {
        const { data } = await api.get("/admin/buyer-requirements");
        setList(data); setSelectedId(data[0]?.id || null);
      }
      setResult(null);
    })();
  }, [mode]);

  useEffect(() => {
    if (!selectedId) return;
    const url = mode === "vehicle" ? `/admin/matching/for-vehicle/${selectedId}` : `/admin/matching/for-requirement/${selectedId}`;
    api.get(url).then(r => setResult(r.data));
  }, [selectedId, mode]);

  return (
    <div data-testid="admin-matching">
      <PageHeader title="Matching Engine" subtitle="Buyer ↔ Seller Network" right={
        <ToolbarSelect value={mode} onChange={setMode} testid="match-mode" options={[
          { v: "vehicle", l: "Suggest buyers for vehicle" },
          { v: "requirement", l: "Suggest cars for buyer" },
        ]} />
      }/>

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-px bg-[#2A2A2A]">
        {/* Source list */}
        <div className="bg-[#0E0E0E] lg:col-span-4 max-h-[78vh] overflow-y-auto">
          <div className="px-4 py-3 border-b border-[#2A2A2A] qd-overline">{mode === "vehicle" ? "Vehicles" : "Requirements"} · {list.length}</div>
          {list.map(item => {
            const active = selectedId === item.id;
            return (
              <button
                key={item.id}
                onClick={() => setSelectedId(item.id)}
                data-testid={`match-pick-${item.id}`}
                className={`block w-full text-left px-4 py-3 border-b border-[#1A1A1A] hover:bg-[#141414] transition-colors ${active ? "bg-[#FF1E1E]/10 border-l-2 border-[#FF1E1E]" : ""}`}
              >
                {mode === "vehicle" ? (
                  <>
                    <div className="font-bold text-white text-sm">{item.make} {item.model}</div>
                    <div className="text-[10px] font-mono-data tracking-widest text-white/45 uppercase">{item.registration_number} · {item.year} · {fmtINR(item.expected_price)}</div>
                  </>
                ) : (
                  <>
                    <div className="font-bold text-white text-sm">{item.full_name}</div>
                    <div className="text-[10px] font-mono-data tracking-widest text-white/45 uppercase">{fmtINR(item.budget_min)}–{fmtINR(item.budget_max)} · {(item.brands || []).slice(0,2).join(", ") || "Any"}</div>
                  </>
                )}
              </button>
            );
          })}
        </div>

        {/* Suggestions */}
        <div className="bg-[#0E0E0E] lg:col-span-8 p-5">
          {!result ? <div className="text-white/40 text-xs font-mono-data">Select an item to see matches.</div> : (
            <div>
              <div className="qd-overline mb-3">{result.suggestions?.length || 0} matches</div>
              <div className="space-y-2">
                {(result.suggestions || []).map((s) => {
                  const isVehicleMode = mode === "vehicle";
                  const target = isVehicleMode ? s.requirement : s.vehicle;
                  return (
                    <div key={target.id} className="bg-[#0F0F0F] border border-[#2A2A2A] p-4">
                      <div className="flex items-center justify-between mb-2">
                        <div>
                          <div className="font-bold text-white">
                            {isVehicleMode ? target.full_name : `${target.make} ${target.model}`}
                          </div>
                          <div className="text-[11px] font-mono-data tracking-widest text-white/45 uppercase">
                            {isVehicleMode
                              ? `${target.contact_phone} · ${fmtINR(target.budget_min)}–${fmtINR(target.budget_max)}`
                              : `${target.year} · ${target.fuel_type} · ${target.transmission} · ${fmtINR(target.expected_price)}`}
                          </div>
                        </div>
                        <Confidence pct={s.match_pct} />
                      </div>
                      <div className="text-[10px] font-mono-data tracking-widest text-white/55 uppercase">
                        {(s.reasons || []).join(" · ")}
                      </div>
                    </div>
                  );
                })}
                {(!result.suggestions || result.suggestions.length === 0) && <div className="text-white/40 text-xs">No strong matches yet.</div>}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function Confidence({ pct }) {
  const c = 2 * Math.PI * 18;
  return (
    <div className="flex items-center gap-2">
      <svg width="44" height="44">
        <circle cx="22" cy="22" r="18" stroke="#2A2A2A" strokeWidth="3" fill="none" />
        <circle cx="22" cy="22" r="18" stroke="#FF1E1E" strokeWidth="3" fill="none"
          strokeDasharray={c} strokeDashoffset={c - (pct/100) * c} strokeLinecap="round" transform="rotate(-90 22 22)" />
      </svg>
      <div className="font-display font-extrabold text-[#FF1E1E] text-lg">{pct}%</div>
    </div>
  );
}
